/**
 * GmailService — client-side service for Gmail integration.
 * All Gmail API calls are proxied through Cloud Functions.
 */

import { httpsCallable } from 'firebase/functions';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db, functions } from '../../firebase';

const getCallable = (name) => httpsCallable(functions, name);

export const GmailService = {
  /**
   * Initiates the Gmail OAuth flow.
   * Calls gmailOAuthInitiate → gets authUrl → redirects browser.
   */
  async connectGmail(schoolId) {
    const fn = getCallable('gmailOAuthInitiate');
    const result = await fn({ schoolId });
    const { authUrl } = result.data;
    window.location.href = authUrl;
  },

  /**
   * Fetches and syncs the teacher's Gmail inbox.
   * @param {Object} options - { schoolId, maxResults, afterDate, pageToken }
   */
  async syncInbox(options = {}) {
    const fn = getCallable('fetchGmailInbox');
    const result = await fn(options);
    return result.data;
  },

  /**
   * Sends a reply to an existing Gmail thread.
   */
  async sendReply({ gmailThreadId, toEmail, subject, body, studentId, parentId, schoolId }) {
    const fn = getCallable('sendGmailMessage');
    const result = await fn({ threadId: gmailThreadId, to: toEmail, subject, body, studentId, parentId, schoolId });
    return result.data;
  },

  /**
   * Sends a new email to a parent.
   */
  async sendEmail({ toEmail, subject, body, studentId, parentId, schoolId }) {
    const fn = getCallable('sendGmailMessage');
    const result = await fn({ to: toEmail, subject, body, studentId, parentId, schoolId });
    return result.data;
  },

  /**
   * Marks a communication document as read in Firestore.
   * Does NOT mark as read in Gmail.
   */
  async markAsRead(communicationId, schoolId) {
    if (!communicationId || !schoolId) return;
    const ref = doc(db, 'schools', schoolId, 'communications', communicationId);
    await updateDoc(ref, {
      isRead: true,
      readAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  },
};

export default GmailService;
